import { useTranslation } from '@thoughtspot/i18n';
import { Checkbox } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getDocumentationLink } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FeatureManagementTranslationKeys } from '../../constants';
import styles from './configure-orgs-modal.module.scss';
const OrgCheckboxList = React.memo(({ orgs, selectedOrgIds, searchValue, showSelectedOnly, onToggleOrg }) => {
    const { t } = useTranslation();
    const filteredOrgs = useMemo(() => {
        let filtered = orgs;
        if (searchValue) {
            filtered = filtered.filter(org => org.name.toLowerCase().includes(searchValue.toLowerCase()));
        }
        if (showSelectedOnly) {
            filtered = filtered.filter(org => selectedOrgIds.has(org.id));
        }
        return filtered;
    }, [orgs, searchValue, showSelectedOnly, selectedOrgIds]);
    if (filteredOrgs.length === 0) {
        if (showSelectedOnly && selectedOrgIds.size === 0) {
            return (React.createElement("div", { className: styles.configOrgsEmptyState },
                React.createElement(Typography, { variant: "body-normal" }, t(FeatureManagementTranslationKeys.MODAL_NO_ORGS_SELECTED))));
        }
        if (searchValue) {
            return (React.createElement("div", { className: styles.configOrgsEmptyState },
                React.createElement(Typography, { variant: "body-normal" }, t(FeatureManagementTranslationKeys.MODAL_NO_SEARCH_RESULTS))));
        }
    }
    return (React.createElement(React.Fragment, null, filteredOrgs.map(org => (React.createElement(Checkbox, { key: org.id, id: org.id, label: org.name, checked: selectedOrgIds.has(org.id), onChange: () => onToggleOrg(org.id), isList: true, className: styles.configOrgsOrgCheckbox, "data-testid": `org-checkbox-${org.id}` })))));
});
export const ConfigureOrgsModal = ({ isOpen, featureName, featureDescription, learnMoreLink, orgs, selectedOrgIds: initialSelectedOrgIds, isLoading = false, isEarlyAccess = false, hasExistingOrgs = false, onDismiss, onSave, }) => {
    const { t } = useTranslation();
    const [selectedOrgIds, setSelectedOrgIds] = useState(new Set(initialSelectedOrgIds));
    const [searchValue, setSearchValue] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setSelectedOrgIds(new Set(initialSelectedOrgIds));
            setSearchValue('');
            setShowSelectedOnly(false);
        }
    }, [isOpen, initialSelectedOrgIds]);
    const handleToggleOrg = useCallback((orgId) => {
        setSelectedOrgIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orgId)) {
                newSet.delete(orgId);
            }
            else {
                newSet.add(orgId);
            }
            return newSet;
        });
    }, []);
    const handleSelectAll = useCallback(() => {
        setSelectedOrgIds(new Set(orgs.map(org => org.id)));
    }, [orgs]);
    const handleClear = useCallback(() => {
        setSelectedOrgIds(new Set());
    }, []);
    const handleSave = useCallback(() => {
        onSave(Array.from(selectedOrgIds));
    }, [selectedOrgIds, onSave]);
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
    }, []);
    const handleToggleShowSelected = useCallback(() => {
        setShowSelectedOnly(prev => !prev);
    }, []);
    const handleLearnMore = useCallback(() => {
        if (learnMoreLink) {
            const resolvedLink = getDocumentationLink(learnMoreLink);
            window.open(resolvedLink, '_blank', 'noopener,noreferrer');
        }
    }, [learnMoreLink]);
    const modalTitle = useMemo(() => {
        const featureParam = { feature: featureName };
        if (isEarlyAccess) {
            return t(FeatureManagementTranslationKeys.MODAL_ENABLE_ORGS_TITLE, featureParam);
        }
        if (hasExistingOrgs) {
            return t(FeatureManagementTranslationKeys.MODAL_CONFIGURE_ORGS_TITLE, featureParam);
        }
        return t(FeatureManagementTranslationKeys.MODAL_ALLOCATE_ORGS_TITLE, featureParam);
    }, [t, featureName, isEarlyAccess, hasExistingOrgs]);
    return (React.createElement(Modal, { isOpen: isOpen, onDismiss: onDismiss, size: ModalSize.MEDIUM, className: styles.configOrgsModal, title: modalTitle, denyButton: learnMoreLink
            ? {
                text: t('LEARN_MORE'),
                onDeny: handleLearnMore,
            }
            : undefined, dismissButton: {
            text: t('CANCEL'),
            onClick: onDismiss,
        }, confirmButton: {
            text: t('SAVE'),
            onConfirm: handleSave,
            isDisabled: isLoading,
        }, "data-testid": "configure-orgs-modal" },
        React.createElement(Vertical, { className: styles.configOrgsModalContent },
            featureDescription && (React.createElement(React.Fragment, null,
                React.createElement(Typography, { variant: "body-normal", ellipsis: {
                        rows: 2,
                        allowMargin: false,
                    }, tooltipConfig: {
                        dataTooltipContent: featureDescription,
                        dataTooltipPosition: 'bottom',
                        dataTooltipAlign: 'center',
                        showTooltipOnlyOnEllipsis: true,
                    }, "data-testid": "feature-description" }, featureDescription),
                React.createElement(Divider, null))),
            React.createElement(Horizontal, { className: styles.configOrgsCountFilterContainer },
                React.createElement(Typography, { variant: "caption", className: styles.configOrgsOrgCount }, t(FeatureManagementTranslationKeys.MODAL_ORGS_COUNT, {
                    count: selectedOrgIds.size,
                })),
                React.createElement("div", { className: styles.configOrgsActionBar },
                    React.createElement("span", { role: "button", tabIndex: 0, onClick: handleSelectAll, onKeyDown: e => e.key === 'Enter' && handleSelectAll(), className: styles.configOrgsHeaderActionLink, "data-testid": "select-all-link" }, t('Select_all')),
                    React.createElement("span", { className: styles.configOrgsActionDivider }),
                    React.createElement("span", { role: "button", tabIndex: 0, onClick: handleClear, onKeyDown: e => e.key === 'Enter' && handleClear(), className: styles.configOrgsHeaderActionLink, "data-testid": "clear-link" }, t('Clear')))),
            React.createElement(SearchBar, { searchValue: searchValue, searchPlaceholder: t('SEARCH_LABEL'), onSearchChange: handleSearchChange, className: styles.configOrgsSearchBar, "data-testid": "search-orgs" }),
            React.createElement(Vertical, { className: styles.configOrgsCheckboxListContainer },
                React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading },
                    React.createElement(Vertical, { className: styles.configOrgsCheckboxList },
                        React.createElement(OrgCheckboxList, { orgs: orgs, selectedOrgIds: selectedOrgIds, searchValue: searchValue, showSelectedOnly: showSelectedOnly, onToggleOrg: handleToggleOrg }))),
                React.createElement(Vertical, { className: styles.configOrgsCheckboxListShowSelectedContainer },
                    React.createElement(Toggle, { checked: showSelectedOnly, onChange: handleToggleShowSelected, labelPosition: "right", "data-testid": "show-selected-toggle", label: t(FeatureManagementTranslationKeys.MODAL_SHOW_SELECTED) }))))));
};
//# sourceMappingURL=configure-orgs-modal.js.map